// Local shortlist store: используется как кэш + единственный источник для гостей.
// При логине данные синхронизируются с сервером (см. useShortlistSync).
import { useMemo } from 'react';
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { emit } from '@/lib/events';

export type LocalShortlist = {
  id: string;              // server id или local-<rand> для гостей
  name: string;
  countryCode: string;
  comuneIstats: string[];
  notes: Record<string, string>;
  createdAt: number;
  updatedAt: number;
  // Sync state
  dirty?: boolean;         // изменения не отправлены на сервер
  localOnly?: boolean;     // создан гостем, ещё не отправлен
};

interface ShortlistState {
  lists: LocalShortlist[];
  activeId: string | null;
  hydrated: boolean;
  setLists: (lists: LocalShortlist[]) => void;
  setActive: (id: string | null) => void;
  createLocal: (name: string, countryCode: string) => string; // returns id
  rename: (id: string, name: string) => void;
  remove: (id: string) => void;
  toggleComune: (id: string, istat: string) => void;
  setNote: (id: string, istat: string, note: string) => void;
  markClean: (id: string, replacement?: Partial<LocalShortlist>) => void;
  setHydrated: (v: boolean) => void;
}

const localId = () => 'local-' + Math.random().toString(36).slice(2, 10);

export const useShortlistStore = create<ShortlistState>()(
  persist(
    (set, get) => ({
      lists: [],
      activeId: null,
      hydrated: false,

      setLists: (lists) => set({ lists }),
      setActive: (activeId) => set({ activeId }),

      createLocal: (name, countryCode) => {
        const id = localId();
        const now = Date.now();
        const list: LocalShortlist = {
          id,
          name: name.trim() || 'Untitled',
          countryCode,
          comuneIstats: [],
          notes: {},
          createdAt: now,
          updatedAt: now,
          dirty: true,
          localOnly: true,
        };
        set({ lists: [list, ...get().lists], activeId: id });
        return id;
      },

      rename: (id, name) =>
        set({
          lists: get().lists.map((l) =>
            l.id === id ? { ...l, name: name.trim() || l.name, updatedAt: Date.now(), dirty: true } : l,
          ),
        }),

      remove: (id) =>
        set((s) => ({
          lists: s.lists.filter((l) => l.id !== id),
          activeId: s.activeId === id ? null : s.activeId,
        })),

      toggleComune: (id, istat) =>
        set({
          lists: get().lists.map((l) => {
            if (l.id !== id) return l;
            const has = l.comuneIstats.includes(istat);
            const comuneIstats = has ? l.comuneIstats.filter((x) => x !== istat) : [...l.comuneIstats, istat];
            const notes = { ...l.notes };
            if (has) delete notes[istat];
            return { ...l, comuneIstats, notes, updatedAt: Date.now(), dirty: true };
          }),
        }),

      setNote: (id, istat, note) =>
        set({
          lists: get().lists.map((l) => {
            if (l.id !== id) return l;
            const notes = { ...l.notes };
            if (note.trim()) notes[istat] = note;
            else delete notes[istat];
            return { ...l, notes, updatedAt: Date.now(), dirty: true };
          }),
        }),

      markClean: (id, replacement) =>
        set({
          lists: get().lists.map((l) =>
            l.id === id ? { ...l, ...replacement, dirty: false, localOnly: false } : l,
          ),
        }),

      setHydrated: (v) => set({ hydrated: v }),
    }),
    {
      name: 'relocation_shortlists_v1',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ lists: state.lists, activeId: state.activeId }),
      onRehydrateStorage: () => (state) => {
        state?.setHydrated(true);
      },
    },
  ),
);

export function useShortlistById(id: string | null) {
  return useShortlistStore((s) => (id ? s.lists.find((l) => l.id === id) ?? null : null));
}

export function useActiveShortlist() {
  return useShortlistStore((s) => (s.activeId ? s.lists.find((l) => l.id === s.activeId) ?? null : null));
}

// Returns the union of all saved comuni istat codes across all lists.
// Selector returns the raw lists array (stable ref) and derives the Set via
// useMemo — иначе создание new Set() внутри селектора ломает Zustand getSnapshot
// caching и вызывает infinite re-render loop (см. BeaconPins).
export function useAllSavedIstats(): Set<string> {
  const lists = useShortlistStore((s) => s.lists);
  return useMemo(() => {
    const result = new Set<string>();
    for (const l of lists) {
      for (const istat of l.comuneIstats) result.add(istat);
    }
    return result;
  }, [lists]);
}

// Gets or creates a default "Saved places" list and returns its id.
// Call from a Zustand action context (not a hook).
export function getOrCreateDefaultList(state: ReturnType<typeof useShortlistStore.getState>): string {
  const existing = state.lists.find((l) => l.id === 'default' || l.name === 'Saved places');
  if (existing) return existing.id;
  return useShortlistStore.getState().createLocal('Saved places', 'IT');
}

// ─── Districts ─────────────────────────────────────────────────────────────
// Районы (SLL / comunità comprensoriale) сохраняются как ОДНА сущность —
// namespaced-код `dist:<code>` внутри того же comuneIstats. Так они без
// изменений переживают sync на бэкенд (IstatList допускает строки ≤20),
// а map-хайлайт коммун их игнорирует (нет совпадения с реальным istat).

export const DIST_PREFIX = 'dist:';

export function districtKey(distCode: string): string {
  return DIST_PREFIX + distCode;
}

// Множество сохранённых district-кодов (без префикса) по всем спискам.
export function useAllSavedDistrictCodes(): Set<string> {
  const lists = useShortlistStore((s) => s.lists);
  return useMemo(() => {
    const result = new Set<string>();
    for (const l of lists) {
      for (const entry of l.comuneIstats) {
        if (entry.startsWith(DIST_PREFIX)) result.add(entry.slice(DIST_PREFIX.length));
      }
    }
    return result;
  }, [lists]);
}

// Save/unsave района в дефолтном списке. Зеркалит useDefaultSave, но ключ —
// namespaced district-код.
export function useDefaultSaveDistrict(distCode: string) {
  const lists = useShortlistStore((s) => s.lists);
  const toggleComune = useShortlistStore((s) => s.toggleComune);
  const createLocal = useShortlistStore((s) => s.createLocal);
  const setActive = useShortlistStore((s) => s.setActive);

  const key = districtKey(distCode);
  const isSaved = lists.some((l) => l.comuneIstats.includes(key));

  const toggle = () => {
    const wasSaved = isSaved;
    const state = useShortlistStore.getState();
    let defaultId = state.lists.find((l) => l.localOnly !== false || l.id === 'default')?.id
      ?? state.lists[0]?.id;
    if (!defaultId) {
      defaultId = createLocal('Saved places', 'IT');
      setActive(defaultId);
    }
    toggleComune(defaultId, key);
    emit('shortlist:save_toggled', { on: !wasSaved, kind: 'district' });
  };

  return { isSaved, toggle };
}

// Hook to save/unsave a comune in the default list with a simple toggle.
export function useDefaultSave(istat: string) {
  const lists = useShortlistStore((s) => s.lists);
  const toggleComune = useShortlistStore((s) => s.toggleComune);
  const createLocal = useShortlistStore((s) => s.createLocal);
  const setActive = useShortlistStore((s) => s.setActive);

  const isSaved = lists.some((l) => l.comuneIstats.includes(istat));

  const toggle = () => {
    const wasSaved = isSaved;
    const state = useShortlistStore.getState();
    let defaultId = state.lists.find((l) => l.localOnly !== false || l.id === 'default')?.id
      ?? state.lists[0]?.id;
    if (!defaultId) {
      defaultId = createLocal('Saved places', 'IT');
      setActive(defaultId);
    }
    toggleComune(defaultId, istat);
    emit('shortlist:save_toggled', { on: !wasSaved });
  };

  return { isSaved, toggle };
}
